<template>
  <el-dialog
    title="环境变量配置"
    :visible.sync="dialogVisible"
    width="960px"
    :before-close="handleClose"
    :close-on-click-modal="false"
    class="env-config-dialog"
  >
    <div v-if="loading" class="loading-container">
      <i class="el-icon-loading"></i>
      <span>加载中...</span>
    </div>

    <div v-else-if="envTemplate.length > 0" class="env-template-list">
      <div
        v-for="component in envTemplate"
        :key="component.componentKey"
        class="component-section"
      >
        <div class="component-header">
          <div>
            <h4>{{ component.componentName }}</h4>
            <div class="component-key">{{ component.componentKey }}</div>
          </div>
        </div>

        <div
          v-for="envVar in component.envVars"
          :key="envVar.key"
          class="env-var-card"
        >
          <div class="env-var-header">
            <div class="env-var-title">
              <span class="env-var-name">{{ envVar.label }}</span>
              <el-tag size="mini" :type="envVar.isChange ? 'success' : 'info'">
                {{ envVar.isChange ? '可配置' : '只读' }}
              </el-tag>
            </div>
            <el-switch
              v-model="envVar.config.enabled"
              :disabled="!envVar.isChange"
              active-text="用户填写"
              inactive-text="不配置"
            />
          </div>

          <div class="env-var-meta">
            <span v-if="envVar.key">
              环境变量: {{ envVar.key }}
            </span>
            <span v-if="envVar.attrValue">
              默认值来源: {{ envVar.attrValue || '-' }}
            </span>
            <span v-if="envVar.value">模板默认值: {{ envVar.value }}</span>
          </div>

          <div v-if="envVar.config.enabled" class="env-var-form">
            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item label="显示名称" label-width="100px">
                  <el-input
                    v-model="envVar.config.displayName"
                    :placeholder="envVar.label"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="输入类型" label-width="100px">
                  <el-select v-model="envVar.config.inputType" style="width: 100%">
                    <el-option label="文本" value="text" />
                    <el-option label="密码" value="password" />
                    <el-option label="数字" value="number" />
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item label="默认值" label-width="100px">
                  <el-input v-model="envVar.config.defaultValue" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="必填" label-width="100px">
                  <el-switch v-model="envVar.config.required" />
                </el-form-item>
              </el-col>
            </el-row>

            <el-form-item label="说明" label-width="100px">
              <el-input
                v-model="envVar.config.description"
                type="textarea"
                :rows="2"
                placeholder="给购买用户的填写提示"
              />
            </el-form-item>

            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item label="校验规则" label-width="100px">
                  <el-input
                    v-model="envVar.config.validationPattern"
                    placeholder="可选，正则表达式"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="校验提示" label-width="100px">
                  <el-input
                    v-model="envVar.config.validationMessage"
                    placeholder="可选，校验失败提示"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="empty-state">
      <p>该套餐关联的模板中没有可配置环境变量</p>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="saving" @click="handleSave">
        保存配置
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import {
  fetchProductPackageEnvTemplate,
  updateProductPackageEnvConfig
} from '@/api/product';

function getEnvKey(envVar) {
  return envVar.key || envVar.attr_name || envVar.attrName || envVar.name || '';
}

function getEnvLabel(envVar) {
  return envVar.label || envVar.name || envVar.display_name || getEnvKey(envVar);
}

function createDefaultConfig(envVar, savedConfig) {
  const validation = savedConfig && savedConfig.validation ? savedConfig.validation : {};
  return {
    enabled: !!savedConfig,
    displayName: savedConfig ? savedConfig.display_name : getEnvLabel(envVar),
    description: savedConfig ? savedConfig.description : '',
    required: savedConfig ? !!savedConfig.required : false,
    inputType: savedConfig ? savedConfig.input_type || 'text' : 'text',
    defaultValue: savedConfig
      ? savedConfig.default_value || ''
      : envVar.value || '',
    validationPattern: validation.pattern || '',
    validationMessage: validation.message || ''
  };
}

export default {
  name: 'EnvConfigDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    marketID: {
      type: String,
      required: true
    },
    productID: {
      type: String,
      default: ''
    },
    packageID: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      saving: false,
      envTemplate: []
    };
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
      if (val) {
        this.loadEnvTemplate();
      }
    }
  },
  methods: {
    normalizeTemplate(response) {
      const templateComponents =
        response &&
        response.envTemplate &&
        response.envTemplate.components
          ? response.envTemplate.components
          : {};
      const savedComponents =
        response &&
        response.userEnvConfig &&
        response.userEnvConfig.components
          ? response.userEnvConfig.components
          : {};

      return Object.keys(templateComponents).map((componentKey) => {
        const component = templateComponents[componentKey];
        const savedConfigs = savedComponents[componentKey] || [];
        const savedMap = savedConfigs.reduce((result, item) => {
          const envKey = item.key || item.attr_name || item.env_name;
          result[envKey] = item;
          return result;
        }, {});

        return {
          componentKey,
          componentName: component.componentName,
          envVars: (component.envVars || []).map((envVar) => ({
            key: getEnvKey(envVar),
            name: getEnvLabel(envVar),
            label: getEnvLabel(envVar),
            value: envVar.value,
            attrName: envVar.attr_name || getEnvKey(envVar),
            attrValue: envVar.attr_value,
            isChange: envVar.is_change !== false,
            config: createDefaultConfig(envVar, savedMap[getEnvKey(envVar)])
          }))
        };
      });
    },

    async loadEnvTemplate() {
      if (!this.productID || !this.packageID) {
        this.envTemplate = [];
        return;
      }

      this.loading = true;
      try {
        const response = await fetchProductPackageEnvTemplate({
          marketID: this.marketID,
          productID: this.productID,
          packageID: this.packageID
        });
        this.envTemplate = this.normalizeTemplate(response);
      } catch (error) {
        this.$message.error('加载环境变量模板失败：' + (error.message || '未知错误'));
      } finally {
        this.loading = false;
      }
    },

    buildPayload() {
      const components = {};

      this.envTemplate.forEach((component) => {
        const selectedEnvVars = component.envVars
          .filter((envVar) => envVar.config.enabled)
          .map((envVar) => {
            const item = {
              key: envVar.key,
              attr_name: envVar.attrName || envVar.key,
              env_name: envVar.key,
              display_name: envVar.config.displayName || envVar.label || envVar.key,
              description: envVar.config.description || '',
              required: !!envVar.config.required,
              input_type: envVar.config.inputType || 'text',
              default_value: envVar.config.defaultValue || ''
            };

            if (envVar.config.validationPattern || envVar.config.validationMessage) {
              item.validation = {
                pattern: envVar.config.validationPattern || '',
                message: envVar.config.validationMessage || ''
              };
            }

            return item;
          });

        if (selectedEnvVars.length > 0) {
          components[component.componentKey] = selectedEnvVars;
        }
      });

      return {
        userEnvConfig: {
          components
        }
      };
    },

    async handleSave() {
      this.saving = true;
      try {
        await updateProductPackageEnvConfig(
          {
            marketID: this.marketID,
            productID: this.productID,
            packageID: this.packageID
          },
          this.buildPayload()
        );
        this.$message.success('环境变量配置保存成功');
        this.$emit('saved');
        this.handleClose();
      } catch (error) {
        this.$message.error('保存环境变量配置失败：' + (error.message || '未知错误'));
      } finally {
        this.saving = false;
      }
    },

    handleClose() {
      this.dialogVisible = false;
      this.$emit('update:visible', false);
    }
  }
};
</script>

<style scoped lang="scss">
.env-config-dialog {
  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 48px 0;
    color: #606266;
  }

  .env-template-list {
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 8px;
  }

  .component-section {
    margin-bottom: 24px;
  }

  .component-header {
    margin-bottom: 12px;

    h4 {
      margin: 0 0 4px;
      color: #303133;
      font-size: 16px;
      font-weight: 600;
    }
  }

  .component-key {
    color: #909399;
    font-size: 12px;
    font-family: 'Courier New', monospace;
  }

  .env-var-card {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 16px;
    margin-bottom: 12px;
    background: #fff;
  }

  .env-var-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 8px;
  }

  .env-var-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .env-var-name {
    font-weight: 600;
    color: #303133;
  }

  .env-var-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 12px;
    color: #909399;
    font-size: 12px;
  }

  .env-var-form {
    padding-top: 8px;
    border-top: 1px dashed #ebeef5;
  }

  .empty-state {
    text-align: center;
    padding: 48px 0;
    color: #909399;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/storeManage/productManage/components/EnvConfigDialog.vue